import axios from "axios";
import { useQuery } from "react-query";

/**
 * Fetch top-N co-occurring tag pairs for the current filter scope.
 * Hits POST /api/insightMatrix/tagCooccurrence — see BE
 * services/_tagCooccurrence/index.js for the algorithm and the rationale
 * behind the Node-side pair counting.
 *
 * Each row in `data.data` carries all four metrics: `count`, `strength`
 * (lift), `overlap` (Jaccard), `predictability` (conditional probability,
 * the stronger of the two directions; `tagA → tagB` reads in that direction).
 */
export const useTagCooccurrence = ({
  activeFilters = [],
  // Raw preset findQuery merged into the BE $match — for constraints
  // activeFilters can't express (e.g. a per-content-type date $or).
  findQuery,
  contentType = "annotations",
  limit = 20,
  excludeSameSubTheme = true,
  pairScope = "cross",
  // Explicit pair shape — { sideA, sideB }, each a SideSpec. Stable
  // identifiers only (never display names): literal _ids (subThemeIds/tagIds)
  // and/or the slug/role vocabulary (subThemeTagIds / taxonomyRoles /
  // excludeSubThemeTagIds — resolved BE-side in _tagCooccurrence). Wins over
  // pairScope when provided.
  sides,
  // Sort spec — `{ [key]: 1 | -1 }` where key is one of count / strength /
  // overlap / predictability. Default count desc when omitted. Passed
  // through to the BE without translation (matches TableElem's
  // backendActiveSort.sort shape).
  sort,
  // Named view: BE owns the recipe (sort default, filter floors, pair
  // shape locks). See services/_tagCooccurrence/byView.js for the catalog.
  view = "mostDiscussed",
  // Force the sideB tag into tagB so the sideA tag (e.g. a signal) is always
  // tagA — lets the caller group results by a known anchor side.
  pinSideBAsTagB = false,
  // Debug switch — zeroes out the view's noise filters (count floor,
  // strength floor, overlap cap) so the analyst can see what's being
  // filtered out.
  removeNoiseFilters = false,
  enabled = true,
}) => {
  const body = {
    contentType,
    activeFilters,
    findQuery,
    limit,
    excludeSameSubTheme,
    pairScope,
    sides,
    sort,
    view,
    removeNoiseFilters,
    pinSideBAsTagB,
  };
  const serialized = JSON.stringify(body);

  return useQuery({
    queryKey: ["useTagCooccurrence", serialized],
    queryFn: () =>
      axios
        .post("/api/insightMatrix/tagCooccurrence", body, {
          headers: { "Content-Type": "application/json" },
        })
        .then((r) => r.data),
    enabled,
    // Cooccurrence is a heavier query than prevalence; keep the cache warm.
    staleTime: 60 * 1000,
  });
};
